// Imported from the leaf module, not the `../shared/index.js` barrel: this
// file is also a frontend entry point (`@borradh-workspace/features/graphics/errors`)
// and the full barrel pulls in server-only database code.
import { isGeminiBillingLimitError } from '../shared/core/gemini-billing-limit.js';
/**
 * Stable, user-reportable error codes for the asynchronous graphics pipeline.
 * Keep codes and user messages backwards compatible: they are persisted on
 * graphic rows and shown in Claire and the processing modal.
 */
export const GraphicGenerationErrorCodes = {
    MODEL_RATE_LIMITED: 'GFX-1001',
    MODEL_BILLING_EXHAUSTED: 'GFX-1002',
    MODEL_GENERATION_FAILED: 'GFX-1003',
    /**
     * The image provider's safety system blocked the generation (ENG-1155).
     * Terminal: the job is not retried, because the same request is blocked
     * again. The owner is told that different wording or imagery may help.
     */
    MODEL_SAFETY_BLOCKED: 'GFX-1004',
    SOURCE_IMAGE_UNAVAILABLE: 'GFX-2001',
    INVALID_GENERATION_INPUT: 'GFX-2002',
    OUTPUT_UPLOAD_FAILED: 'GFX-3001',
    RESULT_SAVE_FAILED: 'GFX-3002',
    UNEXPECTED: 'GFX-9000',
};
export const GraphicGenerationErrorMessages = {
    [GraphicGenerationErrorCodes.MODEL_RATE_LIMITED]: 'Image generation is temporarily busy. Please try again shortly.',
    [GraphicGenerationErrorCodes.MODEL_BILLING_EXHAUSTED]: 'Image generation is temporarily unavailable. Please report this code to support.',
    [GraphicGenerationErrorCodes.MODEL_GENERATION_FAILED]: 'The image service could not create this graphic. Try again or adjust the request.',
    [GraphicGenerationErrorCodes.MODEL_SAFETY_BLOCKED]: "The image service's safety checks blocked this graphic. Different wording or imagery may help.",
    [GraphicGenerationErrorCodes.SOURCE_IMAGE_UNAVAILABLE]: 'One or more selected images could not be used. Choose different uploaded images and try again.',
    [GraphicGenerationErrorCodes.INVALID_GENERATION_INPUT]: 'This graphic request is no longer valid. Review the service and images, then try again.',
    [GraphicGenerationErrorCodes.OUTPUT_UPLOAD_FAILED]: 'The generated graphic could not be saved. Please try again.',
    [GraphicGenerationErrorCodes.RESULT_SAVE_FAILED]: 'The generated graphic finished but its result could not be recorded. Please report this code to support.',
    [GraphicGenerationErrorCodes.UNEXPECTED]: 'An unexpected graphics error occurred. Please report this code to support.',
};
const failure = (code, providerCode, retryable = false) => ({
    code,
    userMessage: GraphicGenerationErrorMessages[code],
    providerCode,
    retryable,
});
/**
 * Provider wording for a safety block, for failures that reach the classifier
 * without the typed `AI_SAFETY_BLOCKED` code (e.g. a raw provider error
 * message). The typed code is the primary signal.
 */
const SAFETY_BLOCK_MESSAGE = /moderation_blocked|content_policy_violation|blocked by content safety/i;
/** "The image "a.jpg" and one of the selected images could not be used…" */
export function rejectedImageMessage(labels) {
    const unique = [...new Set(labels.map((l) => l.trim()).filter(Boolean))];
    const subject = unique.length <= 1
        ? (unique[0] ?? 'one of the selected images')
        : `${unique.slice(0, -1).join(', ')} and ${unique.at(-1)}`;
    return `${subject.charAt(0).toUpperCase()}${subject.slice(1)} could not be used by the image model, even after re-saving ${unique.length > 1 ? 'them' : 'it'}. Choose a different image or regenerate the graphic.`;
}
/** Map internal/provider failures to the stable code exposed to users. */
export function classifyGraphicGenerationFailure({ message, providerCode, publicCode, rejectedImages, }) {
    // A safety block is terminal whatever else is true about the failure:
    // retrying sends the same request and is blocked again.
    if (providerCode === 'AI_SAFETY_BLOCKED' ||
        publicCode === GraphicGenerationErrorCodes.MODEL_SAFETY_BLOCKED ||
        SAFETY_BLOCK_MESSAGE.test(message)) {
        return failure(GraphicGenerationErrorCodes.MODEL_SAFETY_BLOCKED, providerCode);
    }
    if (publicCode) {
        return failure(publicCode, providerCode, true);
    }
    if (isGeminiBillingLimitError(message)) {
        return failure(GraphicGenerationErrorCodes.MODEL_BILLING_EXHAUSTED, providerCode);
    }
    // OpenAI refused a photo sent with the request (400 invalid_image_file).
    // Re-sending the same photo cannot succeed.
    if (/invalid_image_file/i.test(message)) {
        const result = failure(GraphicGenerationErrorCodes.SOURCE_IMAGE_UNAVAILABLE, providerCode);
        return rejectedImages?.length
            ? { ...result, userMessage: rejectedImageMessage(rejectedImages) }
            : result;
    }
    if (providerCode === 'RATE_LIMITED') {
        return failure(GraphicGenerationErrorCodes.MODEL_RATE_LIMITED, providerCode, true);
    }
    if (providerCode === 'VALIDATION_ERROR' || providerCode === 'INVALID_INPUT') {
        return failure(GraphicGenerationErrorCodes.INVALID_GENERATION_INPUT, providerCode);
    }
    if (providerCode === 'NOT_FOUND') {
        return failure(/asset|image|media|source/i.test(message)
            ? GraphicGenerationErrorCodes.SOURCE_IMAGE_UNAVAILABLE
            : GraphicGenerationErrorCodes.INVALID_GENERATION_INPUT, providerCode);
    }
    if (providerCode) {
        return failure(GraphicGenerationErrorCodes.MODEL_GENERATION_FAILED, providerCode, providerCode === 'TIMEOUT' || providerCode === 'EXTERNAL_SERVICE_ERROR');
    }
    if (/no prior slide/i.test(message)) {
        return failure(GraphicGenerationErrorCodes.INVALID_GENERATION_INPUT);
    }
    return failure(GraphicGenerationErrorCodes.UNEXPECTED, undefined, true);
}
//# sourceMappingURL=graphic-generation-errors.js.map